import React, { useMemo, useState } from "react";
import {
  FaInstagram,
  FaFacebookF,
  FaTiktok,
  FaWhatsapp,
  FaMapMarkedAlt,
  FaPhoneAlt,
} from "react-icons/fa";
import { getSessionId } from "../../utils";
import { registrarAcao } from "../../api/public";
import {
  buildLinkHref,
  getActionTypeByLinkType,
  getLinksByType,
  isUuid,
} from "../../utils/storeLinks";
import LinkChoiceModal from "./LinkChoiceModal";

const DEFAULT_WHATSAPP_MESSAGE =
  "Ola! Vi sua loja na Vintree e gostaria de saber mais sobre seus produtos.";

function Footer({ loja }) {
  const sessionId = getSessionId();
  const [pickerState, setPickerState] = useState({
    open: false,
    title: "",
    links: [],
    whatsappText: null,
  });

  const socialConfig = useMemo(
    () => [
      {
        tipo: "whatsapp",
        title: "Escolha um WhatsApp",
        Icon: FaWhatsapp,
        whatsappText: DEFAULT_WHATSAPP_MESSAGE,
      },
      {
        tipo: "instagram",
        title: "Escolha um Instagram",
        Icon: FaInstagram,
      },
      {
        tipo: "facebook",
        title: "Escolha um Facebook",
        Icon: FaFacebookF,
      },
      {
        tipo: "tiktok",
        title: "Escolha um TikTok",
        Icon: FaTiktok,
      },
    ],
    []
  );

  if (!loja) return null;

  const enderecoLinks = getLinksByType(loja, "endereco");
  const geolocLinks = getLinksByType(loja, "geolocalizacao");
  const telefoneLinks = getLinksByType(loja, "telefone");

  const enderecoPrincipal = enderecoLinks[0] || null;
  const telefonePrincipal = telefoneLinks[0] || null;

  const trackLinkClick = (link, origem = "footer") => {
    const payload = {
      session_id: sessionId,
      loja_id: loja.id,
      tipo_acao: getActionTypeByLinkType(link?.tipo),
      origem,
      link_tipo: link?.tipo || null,
      link_label: link?.label || null,
      link_valor: link?.valor || null,
    };

    if (isUuid(link?.id)) {
      payload.loja_link_id = link.id;
    }

    registrarAcao(payload).catch((err) =>
      console.error("Erro ao registrar acao de link:", err)
    );
  };

  const openPicker = ({ title, links, whatsappText = null }) => {
    setPickerState({
      open: true,
      title,
      links,
      whatsappText,
    });
  };

  const closePicker = () => {
    setPickerState({ open: false, title: "", links: [], whatsappText: null });
  };

  const renderSocialAction = ({ tipo, title, Icon, whatsappText = null }) => {
    const links = getLinksByType(loja, tipo);
    if (!links.length) return null;

    if (links.length === 1) {
      const link = links[0];
      const href = buildLinkHref(link, { whatsappText });
      if (!href) return null;

      return (
        <a
          key={`${tipo}-${link.id}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLinkClick(link, "footer")}
        >
          <Icon className="text-gray-600 text-2xl hover:text-black" />
        </a>
      );
    }

    return (
      <button
        key={`${tipo}-multi`}
        type="button"
        onClick={() => openPicker({ title, links, whatsappText })}
      >
        <Icon className="text-gray-600 text-2xl hover:text-black" />
      </button>
    );
  };

  const handleEnderecoClick = (event) => {
    if (!geolocLinks.length) return;

    if (geolocLinks.length === 1) {
      trackLinkClick(geolocLinks[0], "footer_endereco");
      return;
    }

    event.preventDefault();
    openPicker({ title: "Escolha um endereco", links: geolocLinks });
  };

  const enderecoHref = geolocLinks.length > 0 ? buildLinkHref(geolocLinks[0]) || "#" : "#";

  return (
    <>
      <footer className="bg-gray-100 mt-16 py-6 px-4 border-t border-gray-300 text-sm text-gray-700">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-start gap-6">
          <div>
            <p className="font-semibold text-gray-900">{loja.nome_fantasia}</p>
            {enderecoPrincipal && (
              <p className="flex items-center gap-2 mt-1">
                <FaMapMarkedAlt className="text-gray-500" />
                {geolocLinks.length > 0 ? (
                  <a
                    href={enderecoHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    onClick={handleEnderecoClick}
                  >
                    {enderecoPrincipal.valor}
                  </a>
                ) : (
                  <span>{enderecoPrincipal.valor}</span>
                )}
              </p>
            )}
            {telefonePrincipal && (
              <p className="flex items-center gap-2 mt-1">
                <FaPhoneAlt className="text-gray-500" />
                <a
                  href={buildLinkHref(telefonePrincipal)}
                  className="hover:underline"
                  onClick={() => trackLinkClick(telefonePrincipal, "footer_telefone")}
                >
                  {telefonePrincipal.valor}
                </a>
              </p>
            )}
          </div>

          <div className="flex gap-4">{socialConfig.map(renderSocialAction)}</div>
        </div>

        <hr className="my-6 border-gray-300" />

        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-gray-600">
          <a
            href="https://vintree.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-600 font-semibold hover:underline"
          >
            Crie sua propria Vintree
          </a>
          <span>
            Â© 2025 Vintree. Concebido e desenvolvido por{" "}
            <span className="font-semibold">LucasLmir</span>
          </span>
        </div>
      </footer>

      <LinkChoiceModal
        open={pickerState.open}
        onClose={closePicker}
        title={pickerState.title}
        links={pickerState.links}
        onSelect={(link) => {
          const href = buildLinkHref(link, {
            whatsappText: pickerState.whatsappText,
          });

          if (!href) return;

          trackLinkClick(link, "footer");
          window.open(href, "_blank", "noopener,noreferrer");
          closePicker();
        }}
      />
    </>
  );
}

export default Footer;
